import type { AdminPermission } from "../lib/acl/permissions";

/**
 * Capability-based nav gate for surfaces that admit non-admin ACL holders.
 * Resolved asynchronously in `useVisibleSections` against the Asset Management
 * Center capability contract (`GET /api/content/capabilities`), because feature
 * grants like `media.library.use` are NOT carried on the synchronous `/api/me`
 * `AuthUser.permissions` (which only holds `AdminPermission`s).
 *
 * - `"amc-access"` — visible when the user has AMC access (admin OR `media.library.use`).
 * - `"amc-tab:<tab>"` — visible when that tab is in the user's `allowedTabs`.
 */
export type NavCapabilityGate =
  | "amc-access"
  | "amc-tab:local"
  | "amc-tab:brand"
  | "amc-tab:app-logos"
  | "amc-tab:avatars"
  | "amc-tab:backups";

export type NavLinkItem = {
  id: string;
  label: string;
  path: string;
  icon: string;
  disabled?: boolean;
  requiresRole?: "admin" | "user";
  requiresPermission?: AdminPermission;
  requiresFeature?: "wsdashboard" | "webconsole";
  /** Requires a system-wide app to be installed (e.g., "phpmyadmin"). */
  requiresApp?: string;
  /** Requires an async capability (e.g., AMC access for ACL `media.library.use` users). */
  requiresCapability?: NavCapabilityGate;
  external?: boolean;
  /** When true, NavLink uses prefix matching so sub-routes also show as active. */
  matchSubRoutes?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  icon?: string;
  /** Optional path for the group header - makes it a navigable link */
  path?: string;
  requiresRole?: "admin" | "user";
  requiresPermission?: AdminPermission;
  requiresFeature?: "wsdashboard" | "webconsole";
  /** Requires an async capability (e.g., AMC access for ACL `media.library.use` users). */
  requiresCapability?: NavCapabilityGate;
  items: NavLinkItem[];
  /** Nested sub-groups — enables hierarchical navigation (e.g., Control Center domains under Streaming Dashboard) */
  groups?: NavGroup[];
};

export type NavSection = {
  id: string;
  label: string;
  icon?: string;
  items?: NavLinkItem[];
  groups?: NavGroup[];
};

const docAppPath = (category: string, slug: string) => `/apps/${category}/${slug}`;

// Canonical software taxonomy mirrors qb.web docs /docs/applications/_meta.json (FULL parity)
const softwareGroups: NavGroup[] = [
  {
    id: "media-management",
    label: "Media Management",
    icon: "lucide:sliders-horizontal",
    items: [
      { id: "media-management-bazarr", label: "Bazarr", path: docAppPath("media-management", "bazarr"), icon: "lucide:text", disabled: true },
      { id: "media-management-lazylibrarian", label: "LazyLibrarian", path: docAppPath("media-management", "lazylibrarian"), icon: "lucide:book-open", disabled: true },
      { id: "media-management-lidarr", label: "Lidarr", path: docAppPath("media-management", "lidarr"), icon: "lucide:music", disabled: true },
      { id: "media-management-listenarr", label: "Listenarr", path: docAppPath("media-management", "listenarr"), icon: "lucide:book-headphones", disabled: true },
      { id: "media-management-medusa", label: "Medusa", path: docAppPath("media-management", "medusa"), icon: "lucide:activity", disabled: true },
      { id: "media-management-mylar3", label: "Mylar3", path: docAppPath("media-management", "mylar3"), icon: "lucide:book", disabled: true },
      { id: "media-management-radarr", label: "Radarr", path: docAppPath("media-management", "radarr"), icon: "lucide:film", disabled: true },
      { id: "media-management-readarr", label: "Readarr", path: docAppPath("media-management", "readarr"), icon: "lucide:book-open", disabled: true },
      { id: "media-management-sickchill", label: "SickChill", path: docAppPath("media-management", "sickchill"), icon: "lucide:wind", disabled: true },
      { id: "media-management-sickgear", label: "SickGear", path: docAppPath("media-management", "sickgear"), icon: "lucide:settings", disabled: true },
      { id: "media-management-sonarr", label: "Sonarr", path: docAppPath("media-management", "sonarr"), icon: "lucide:waves", disabled: true }
    ]
  },
  {
    id: "media-servers",
    label: "Media Servers",
    icon: "lucide:server",
    items: [
      { id: "media-servers-airsonic", label: "Airsonic", path: docAppPath("media-servers", "airsonic"), icon: "lucide:music-2", disabled: true },
      { id: "media-servers-audiobookshelf", label: "AudioBookShelf", path: docAppPath("media-servers", "audiobookshelf"), icon: "lucide:book-headphones", disabled: true },
      { id: "media-servers-dispatcharr", label: "Dispatcharr", path: docAppPath("media-servers", "dispatcharr"), icon: "lucide:tv-2", disabled: true },
      { id: "media-servers-emby", label: "Emby", path: docAppPath("media-servers", "emby"), icon: "simple-icons:emby", disabled: true },
      { id: "media-servers-jellyfin", label: "Jellyfin", path: docAppPath("media-servers", "jellyfin"), icon: "simple-icons:jellyfin", disabled: true },
      { id: "media-servers-plex", label: "Plex", path: docAppPath("media-servers", "plex"), icon: "lucide:tv", disabled: true },
      { id: "media-servers-subsonic", label: "Subsonic", path: docAppPath("media-servers", "subsonic"), icon: "lucide:radio", disabled: true },
      { id: "media-servers-tautulli", label: "Tautulli", path: docAppPath("media-servers", "tautulli"), icon: "lucide:line-chart", disabled: true },
      { id: "media-servers-xteve", label: "xTeVe", path: docAppPath("media-servers", "xteve"), icon: "lucide:waves", disabled: true }
    ]
  },
  {
    id: "media-requests",
    label: "Media Requests",
    icon: "lucide:inbox",
    items: [
      { id: "media-requests-jellyseerr", label: "Jellyseerr", path: docAppPath("media-requests", "jellyseerr"), icon: "lucide:search", disabled: true },
      { id: "media-requests-ombi", label: "Ombi", path: docAppPath("media-requests", "ombi"), icon: "lucide:mail", disabled: true },
      { id: "media-requests-overseerr", label: "Overseerr", path: docAppPath("media-requests", "overseerr"), icon: "lucide:binoculars", disabled: true },
      { id: "media-requests-requestrr", label: "Requestrr", path: docAppPath("media-requests", "requestrr"), icon: "lucide:message-square", disabled: true },
      { id: "media-requests-seerr", label: "Seerr", path: docAppPath("media-requests", "seerr"), icon: "lucide:search", disabled: true }
    ]
  },
  {
    id: "download-clients",
    label: "Download Clients",
    icon: "lucide:download",
    items: [
      { id: "download-clients-deluge", label: "Deluge", path: docAppPath("download-clients", "deluge"), icon: "lucide:waves", disabled: true },
      { id: "download-clients-flood", label: "Flood", path: docAppPath("download-clients", "flood"), icon: "lucide:cloud-rain", disabled: true },
      { id: "download-clients-jdownloader", label: "JDownloader", path: docAppPath("download-clients", "jdownloader"), icon: "lucide:download", disabled: true },
      { id: "download-clients-nzbget", label: "NZBGet", path: docAppPath("download-clients", "nzbget"), icon: "lucide:download-cloud", disabled: true },
      { id: "download-clients-pyload", label: "pyLoad", path: docAppPath("download-clients", "pyload"), icon: "lucide:archive", disabled: true },
      { id: "download-clients-qbittorrent", label: "qBittorrent", path: docAppPath("download-clients", "qbittorrent"), icon: "lucide:download", disabled: true },
      { id: "download-clients-rtorrent", label: "rTorrent", path: docAppPath("download-clients", "rtorrent"), icon: "lucide:radio", disabled: true },
      { id: "download-clients-rutorrent", label: "ruTorrent", path: docAppPath("download-clients", "rutorrent"), icon: "lucide:repeat", disabled: true },
      { id: "download-clients-sabnzbd", label: "SABnzbd", path: docAppPath("download-clients", "sabnzbd"), icon: "lucide:cloud-download", disabled: true },
      { id: "download-clients-transmission", label: "Transmission", path: docAppPath("download-clients", "transmission"), icon: "lucide:radio", disabled: true }
    ]
  },
  {
    id: "indexers",
    label: "Indexers & Trackers",
    icon: "lucide:radar",
    items: [
      { id: "indexers-autobrr", label: "Autobrr", path: docAppPath("indexers", "autobrr"), icon: "lucide:disc", disabled: true },
      { id: "indexers-autodl", label: "autodl-irssi", path: docAppPath("indexers", "autodl"), icon: "lucide:terminal", disabled: true },
      { id: "indexers-jackett", label: "Jackett", path: docAppPath("indexers", "jackett"), icon: "lucide:search", disabled: true },
      { id: "indexers-nzbhydra2", label: "NZBHydra2", path: docAppPath("indexers", "nzbhydra2"), icon: "lucide:search-check", disabled: true },
      { id: "indexers-prowlarr", label: "Prowlarr", path: docAppPath("indexers", "prowlarr"), icon: "lucide:compass", disabled: true }
    ]
  },
  {
    id: "automation",
    label: "Automation & Tools",
    icon: "lucide:wand-2",
    items: [
      { id: "automation-autoscan", label: "Autoscan", path: docAppPath("automation", "autoscan"), icon: "lucide:scan", disabled: true },
      { id: "automation-filebot", label: "FileBot", path: docAppPath("automation", "filebot"), icon: "lucide:bot", disabled: true },
      { id: "automation-flaresolverr", label: "FlareSolverr", path: docAppPath("automation", "flaresolverr"), icon: "lucide:shield", disabled: true },
      { id: "automation-flexget", label: "FlexGet", path: docAppPath("automation", "flexget"), icon: "lucide:workflow", disabled: true },
      { id: "automation-headphones", label: "Headphones", path: docAppPath("automation", "headphones"), icon: "lucide:headphones", disabled: true },
      { id: "automation-notifiarr", label: "Notifiarr", path: docAppPath("automation", "notifiarr"), icon: "lucide:bell", disabled: true },
      { id: "automation-seedcross", label: "SeedCross", path: docAppPath("automation", "seedcross"), icon: "lucide:git-merge", disabled: true },
      { id: "automation-unmanic", label: "Unmanic", path: docAppPath("automation", "unmanic"), icon: "lucide:film", disabled: true },
      { id: "automation-unpackerr", label: "Unpackerr", path: docAppPath("automation", "unpackerr"), icon: "lucide:package-open", disabled: true }
    ]
  },
  {
    id: "file-management",
    label: "File Management",
    icon: "lucide:folder",
    items: [
      { id: "file-management-btsync", label: "Resilio Sync", path: docAppPath("file-management", "btsync"), icon: "lucide:refresh-ccw", disabled: true },
      { id: "file-management-duplicati", label: "Duplicati", path: docAppPath("file-management", "duplicati"), icon: "lucide:cloud", disabled: true },
      { id: "file-management-filebrowser", label: "FileBrowser", path: docAppPath("file-management", "filebrowser"), icon: "lucide:folder", disabled: true },
      { id: "file-management-nextcloud", label: "Nextcloud", path: docAppPath("file-management", "nextcloud"), icon: "lucide:cloud", disabled: true },
      { id: "file-management-rclone", label: "Rclone", path: docAppPath("file-management", "rclone"), icon: "lucide:cloud", disabled: true },
      { id: "file-management-syncthing", label: "Syncthing", path: docAppPath("file-management", "syncthing"), icon: "lucide:refresh-cw", disabled: true }
    ]
  },
  {
    id: "readers",
    label: "E-Books & Comics",
    icon: "lucide:book",
    items: [
      { id: "readers-calibre", label: "Calibre", path: docAppPath("readers", "calibre"), icon: "lucide:book-open" , disabled: true },
      { id: "readers-kavita", label: "Kavita", path: docAppPath("readers", "kavita"), icon: "lucide:book-open", disabled: true },
      { id: "readers-komga", label: "Komga", path: docAppPath("readers", "komga"), icon: "lucide:book", disabled: true }
    ]
  },
  {
    id: "remote-access",
    label: "Remote Access",
    icon: "lucide:monitor",
    items: [
      { id: "remote-access-novnc", label: "noVNC", path: docAppPath("remote-access", "novnc"), icon: "lucide:mouse-pointer-2", disabled: true },
      { id: "remote-access-webconsole", label: "Web Console", path: "/system/console", icon: "lucide:terminal", requiresRole: "admin", requiresFeature: "webconsole" },
      { id: "remote-access-x2go", label: "X2Go", path: docAppPath("remote-access", "x2go"), icon: "lucide:laptop", disabled: true }
    ]
  },
  {
    id: "communication",
    label: "Communication",
    icon: "lucide:messages-square",
    items: [
      { id: "communication-quassel", label: "Quassel", path: docAppPath("communication", "quassel"), icon: "lucide:message-circle", disabled: true },
      { id: "communication-thelounge", label: "The Lounge", path: docAppPath("communication", "thelounge"), icon: "lucide:messages-square", disabled: true },
      { id: "communication-znc", label: "ZNC", path: docAppPath("communication", "znc"), icon: "lucide:message-square", disabled: true }
    ]
  },
  {
    id: "utilities",
    label: "Utilities & System",
    icon: "lucide:settings",
    items: [
      { id: "utilities-fail2ban", label: "Fail2ban", path: docAppPath("utilities", "fail2ban"), icon: "lucide:ban", disabled: true },
      { id: "utilities-lecert", label: "Let's Encrypt", path: "/system/ssl", icon: "lucide:lock", requiresRole: "admin" },
      { id: "utilities-netdata", label: "Netdata", path: docAppPath("utilities", "netdata"), icon: "lucide:activity", disabled: true },
      { id: "utilities-phpmyadmin", label: "phpMyAdmin", path: docAppPath("utilities", "phpmyadmin"), icon: "lucide:database", disabled: true },
      { id: "utilities-vpn", label: "VPN", path: "/system/vpn", icon: "lucide:shield-check", requiresRole: "admin", requiresApp: "wireguard" },
      { id: "utilities-wireguard", label: "WireGuard", path: "/system/vpn", icon: "lucide:shield", requiresRole: "admin", requiresApp: "wireguard" }
    ]
  }
];

/**
 * Category metadata for Software Quick Navigation.
 * Used by SidebarNav to label dynamic groups built from quickNavItems.
 * Derived from the canonical softwareGroups taxonomy above.
 */
export const softwareCategoryMeta: Record<string, { label: string; icon: string }> =
  Object.fromEntries(
    softwareGroups.map((g) => [g.id, { label: g.label, icon: g.icon ?? "lucide:folder" }])
  );

const SOFTWARE_CATEGORY_ITEM_TOTALS: Record<string, number> = {
  "media-management": 11,
  "media-servers": 9,
  "media-requests": 5,
  "download-clients": 10,
  indexers: 5,
  automation: 9,
  "file-management": 6,
  readers: 3,
  "remote-access": 3,
  communication: 3,
  utilities: 6
};

if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
  const expectedCategoryCount = Object.keys(SOFTWARE_CATEGORY_ITEM_TOTALS).length;
  if (softwareGroups.length !== expectedCategoryCount) {
    throw new Error(`Software nav taxonomy mismatch: expected ${expectedCategoryCount} categories, found ${softwareGroups.length}`);
  }

  const missingCategories = Object.keys(SOFTWARE_CATEGORY_ITEM_TOTALS).filter(
    (id) => !softwareGroups.some((group) => group.id === id)
  );
  const extraCategories = softwareGroups
    .map((group) => group.id)
    .filter((id) => !Object.prototype.hasOwnProperty.call(SOFTWARE_CATEGORY_ITEM_TOTALS, id));

  if (missingCategories.length || extraCategories.length) {
    throw new Error(
      `Software nav taxonomy ids out of sync. Missing: ${missingCategories.join(", ") || "none"}. Extra: ${extraCategories.join(", ") || "none"}.`
    );
  }

  const mismatches = softwareGroups
    .map((group) => ({
      id: group.id,
      expected: SOFTWARE_CATEGORY_ITEM_TOTALS[group.id],
      actual: group.items.length
    }))
    .filter((entry) => entry.expected !== entry.actual);

  if (mismatches.length) {
    const details = mismatches.map((entry) => `${entry.id} expected ${entry.expected} got ${entry.actual}`).join(", ");
    throw new Error(`Software nav taxonomy item counts out of sync: ${details}`);
  }
}

export const navSections: NavSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "lucide:layout-dashboard",
    items: [
      {
        id: "app-dashboard",
        label: "App Dashboard",
        path: "/dashboard",
        icon: "lucide:layout-dashboard"
      },
      {
        id: "system-dashboard",
        label: "System Dashboard",
        path: "/system/dashboard",
        icon: "lucide:layers",
        requiresRole: "admin"
      }
    ],
    groups: [
      {
        id: "streaming-dashboard",
        label: "Streaming Dashboard",
        icon: "lucide:tv",
        path: "/streaming/dashboard",
        requiresRole: "admin",
        requiresFeature: "wsdashboard",
        items: [
          {
            id: "wsd-analytics",
            label: "Watch Analytics",
            path: "/streaming/dashboard#analytics",
            icon: "lucide:bar-chart-2",
            requiresRole: "admin",
            requiresFeature: "wsdashboard"
          },
          {
            id: "wsd-logs",
            label: "Activity Logs",
            path: "/streaming/dashboard#logs",
            icon: "lucide:scroll-text",
            requiresRole: "admin",
            requiresFeature: "wsdashboard"
          },
          {
            id: "wsd-settings",
            label: "Settings",
            path: "/streaming/dashboard#settings",
            icon: "lucide:settings",
            requiresRole: "admin",
            requiresFeature: "wsdashboard"
          }
        ],
        groups: [
          {
            id: "wsd-control-center",
            label: "Control Center",
            icon: "lucide:gauge",
            path: "/streaming/dashboard/control-center",
            requiresRole: "admin",
            requiresFeature: "wsdashboard",
            items: [
              { id: "cc-overview", label: "Overview", path: "/streaming/dashboard/control-center", icon: "lucide:gauge", requiresRole: "admin", requiresFeature: "wsdashboard" },
              { id: "cc-server-users", label: "Server Users", path: "/streaming/dashboard/control-center/server-users", icon: "lucide:users", requiresRole: "admin", requiresFeature: "wsdashboard" },
              { id: "cc-devices", label: "Devices", path: "/streaming/dashboard/control-center/devices", icon: "lucide:monitor-smartphone", requiresRole: "admin", requiresFeature: "wsdashboard" },
              { id: "cc-history-analytics", label: "History & Analytics", path: "/streaming/dashboard/control-center/history", icon: "lucide:bar-chart-3", requiresRole: "admin", requiresFeature: "wsdashboard" },
              { id: "cc-media-portal", label: "Media Portal", path: "/streaming/dashboard/control-center/media", icon: "lucide:clapperboard", requiresRole: "admin", requiresFeature: "wsdashboard", matchSubRoutes: true }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "settings",
    label: "Settings",
    icon: "lucide:settings",
    items: [
      {
        id: "settings-email",
        label: "Email Settings",
        path: "/settings/email",
        icon: "lucide:mail",
        requiresRole: "admin",
        requiresPermission: "admin.settings.read"
      },
      {
        id: "settings-notifications",
        label: "Notification Settings",
        path: "/settings/notifications",
        icon: "lucide:bell",
        requiresRole: "admin",
        requiresPermission: "admin.settings.read"
      }
    ],
    groups: [
      {
        id: "settings-general",
        label: "General Settings",
        icon: "lucide:feather",
        path: "/settings/general",
        requiresRole: "admin",
        requiresPermission: "admin.settings.read",
        items: [
          { id: "gs-instance-settings", label: "Instance settings", path: "/settings/general#instance-settings", icon: "lucide:info", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-timezone-locale", label: "Timezone & locale", path: "/settings/general#timezone-locale", icon: "lucide:clock", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-sessions-security", label: "Sessions & security", path: "/settings/general#sessions-security", icon: "lucide:lock", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-display-preferences", label: "Display preferences", path: "/settings/general#display-preferences", icon: "lucide:monitor", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-additional-dashboard-settings", label: "Additional dashboard settings", path: "/settings/general#additional-dashboard-settings", icon: "lucide:sliders-horizontal", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-uploads", label: "Uploads", path: "/settings/general#uploads", icon: "lucide:upload", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-branding", label: "Branding", path: "/settings/general#branding", icon: "lucide:palette", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-auth-pages", label: "Auth pages", path: "/settings/general#auth-pages", icon: "lucide:log-in", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "gs-feature-flags", label: "Feature flags", path: "/settings/general#feature-flags", icon: "lucide:toggle-right", requiresRole: "admin", requiresPermission: "admin.settings.read" }
        ]
      },
      {
        id: "settings-sessions",
        label: "Session Settings",
        icon: "lucide:watch",
        path: "/settings/sessions",
        requiresRole: "admin",
        requiresPermission: "admin.sessions.read",
        items: [
          { id: "ss-timeouts", label: "Timeouts", path: "/settings/sessions#timeouts", icon: "lucide:timer", requiresRole: "admin", requiresPermission: "admin.sessions.read" },
          { id: "ss-persistence-cookies", label: "Persistence & Cookies", path: "/settings/sessions#persistence-cookies", icon: "lucide:cookie", requiresRole: "admin", requiresPermission: "admin.sessions.read" },
          { id: "ss-concurrency", label: "Concurrency", path: "/settings/sessions#concurrency", icon: "lucide:git-branch", requiresRole: "admin", requiresPermission: "admin.sessions.read" },
          { id: "ss-session-operations", label: "Session Operations", path: "/settings/sessions#session-operations", icon: "lucide:settings-2", requiresRole: "admin", requiresPermission: "admin.sessions.read" }
        ]
      },
      {
        id: "settings-security",
        label: "Security Settings",
        icon: "lucide:shield",
        path: "/settings/security",
        requiresRole: "admin",
        requiresPermission: "admin.settings.read",
        items: [
          { id: "sec-firewall-rules", label: "Firewall Rules", path: "/settings/security?tab=firewall", icon: "lucide:flame", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "sec-geo-lock", label: "Geo-Lock", path: "/settings/security?tab=geo-lock", icon: "lucide:globe-lock", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "sec-geo-block", label: "Geo-Block", path: "/settings/security?tab=geo-block", icon: "lucide:globe", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "sec-fail2ban", label: "Fail2Ban", path: "/settings/security?tab=fail2ban", icon: "lucide:shield-ban", requiresRole: "admin", requiresPermission: "admin.settings.read" }
        ]
      },
      {
        id: "settings-registration",
        label: "Registration Settings",
        icon: "lucide:clipboard-list",
        path: "/settings/registration",
        requiresRole: "admin",
        requiresPermission: "admin.settings.read",
        items: [
          { id: "reg-activation", label: "Activation", path: "/settings/registration#activation", icon: "lucide:check-circle", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "reg-quotas", label: "Quotas", path: "/settings/registration#quotas", icon: "lucide:gauge", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "reg-default-group", label: "Default Group", path: "/settings/registration#default-group", icon: "lucide:users", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "reg-username-policy", label: "Username Policy", path: "/settings/registration#username-policy", icon: "lucide:at-sign", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "reg-password-policy", label: "Password Policy", path: "/settings/registration#password-policy", icon: "lucide:key", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "reg-welcome-email", label: "Welcome Email", path: "/settings/registration#welcome-email", icon: "lucide:mail-check", requiresRole: "admin", requiresPermission: "admin.settings.read" }
        ]
      }
    ]
  },
  {
    id: "user-management",
    label: "User Management",
    icon: "lucide:users",
    items: [
      {
        id: "users-admin",
        label: "User Admin",
        path: "/users/admin",
        icon: "lucide:user-check",
        requiresRole: "admin",
        requiresPermission: "admin.users.read"
      },
      {
        id: "users-groups",
        label: "User Groups",
        path: "/users/groups",
        icon: "lucide:users",
        requiresRole: "admin",
        requiresPermission: "admin.groups.read"
      },
      {
        id: "users-roles",
        label: "User Roles",
        path: "/users/roles",
        icon: "lucide:shield-check",
        requiresRole: "admin",
        requiresPermission: "admin.roles.read"
      }
    ]
  },
  {
    id: "system",
    label: "System",
    icon: "lucide:server",
    items: [
      {
        id: "system-db-management",
        label: "DB Management",
        path: "/phpmyadmin/",
        icon: "lucide:database",
        requiresRole: "admin",
        requiresPermission: "admin.system.database",
        requiresApp: "phpmyadmin",
        external: true
      },
      {
        id: "system-console",
        label: "Web Console",
        path: "/system/console",
        icon: "lucide:terminal",
        requiresRole: "admin",
        requiresPermission: "admin.system.console",
        requiresFeature: "webconsole"
      },
      {
        id: "system-api-settings",
        label: "API Control",
        path: "/system/api",
        icon: "lucide:key-round",
        requiresRole: "admin",
        requiresPermission: "admin.system.api"
      },
      {
        id: "system-app-templates",
        label: "App Templates",
        path: "/system/app-templates",
        icon: "lucide:package-open",
        requiresRole: "admin",
        requiresPermission: "admin.settings.update"
      },
      {
        id: "system-ssl-control",
        label: "SSL Control",
        path: "/system/ssl",
        icon: "lucide:award",
        requiresRole: "admin",
        requiresPermission: "admin.system.ssl"
      },
      {
        id: "system-vpn-control",
        label: "VPN Control",
        path: "/system/vpn",
        icon: "lucide:compass",
        requiresRole: "admin",
        requiresPermission: "admin.system.vpn",
        requiresApp: "wireguard"
      },
      {
        id: "system-network",
        label: "Network",
        path: "/system/network",
        icon: "lucide:gauge",
        requiresRole: "admin"
      },
      {
        id: "system-news",
        label: "News",
        path: "/system/news",
        icon: "lucide:megaphone",
        requiresRole: "admin",
        requiresPermission: "admin.system.update"
      },
      {
        id: "system-changelog",
        label: "Changelog",
        path: "/system/changelog",
        icon: "lucide:scroll-text",
        requiresRole: "admin",
        requiresPermission: "admin.system.update"
      },
      {
        id: "system-logs",
        label: "System Logs",
        path: "/system/logs",
        icon: "lucide:align-left",
        requiresRole: "admin",
        requiresPermission: "admin.system.logs"
      },
      {
        id: "system-documentation",
        label: "Documentation",
        path: "https://v3.quickbox.io/docs",
        icon: "lucide:book",
        requiresRole: "admin",
        external: true
      }
    ]
  },
  {
    id: "content",
    label: "Content",
    icon: "lucide:images",
    groups: [
      {
        id: "content-media-library",
        label: "Asset Management Center",
        icon: "lucide:images",
        path: "/content/media",
        // Visible to admins AND ACL `media.library.use` users (the page itself
        // gates each tab; here we mirror that so the link surfaces for both).
        requiresCapability: "amc-access",
        items: [
          { id: "ml-local-assets", label: "Local Assets", path: "/content/media", icon: "lucide:image", requiresCapability: "amc-tab:local" },
          { id: "ml-brand-assets", label: "Brand Assets", path: "/content/media?tab=brand", icon: "lucide:palette", requiresRole: "admin", requiresPermission: "admin.settings.read" },
          { id: "ml-app-logos", label: "Application Logos", path: "/content/media?tab=app-logos", icon: "lucide:package-open", requiresRole: "admin" },
          { id: "ml-avatars", label: "Avatars", path: "/content/media?tab=avatars", icon: "lucide:user-round", requiresCapability: "amc-tab:avatars" },
          { id: "ml-app-backups", label: "Application Backups", path: "/content/media?tab=backups", icon: "lucide:archive", requiresCapability: "amc-tab:backups" }
        ]
      }
    ]
  },
  {
    id: "software",
    label: "Software",
    icon: "lucide:package",
    groups: softwareGroups
  }
];

export type NavItemWithSection = NavLinkItem & {
  sectionLabel: string;
  groupId?: string;
  groupLabel?: string;
};

const flattenGroups = (groups: NavGroup[]): Array<NavLinkItem & { groupId: string; groupLabel: string }> =>
  groups.flatMap((group) => [
    ...group.items.map((item) => ({ ...item, groupId: group.id, groupLabel: group.label })),
    ...(group.groups ? flattenGroups(group.groups) : [])
  ]);

export const navItems: NavItemWithSection[] = navSections.flatMap((section) => {
  const sectionItems = section.items ?? [];
  const groupItems = section.groups ? flattenGroups(section.groups) : [];

  return [...sectionItems, ...groupItems].map((item) => ({
    ...item,
    sectionLabel: section.label
  }));
});
